import CloseIcon from '@mui/icons-material/Close'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  SxProps,
  Theme,
} from '@mui/material'
import { createContext, ReactNode, useCallback, useContext, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface ModalOptions {
  id?: number
  /**
   * Opt-in de-duplication key. While a modal with this key is open, further
   * `modal()` calls carrying it are ignored, so a double-click on the button
   * that opens it cannot stack two copies of the same dialog.
   */
  modalKey?: string
  title: ReactNode
  description: ReactNode
  sx?: SxProps<Theme>
  hideActions?: boolean
  hideCancelButton?: boolean
  hideConfirmButton?: boolean
  confirmLabel?: string
  cancelLabel?: string
  width?: number
  onBeforeClose?: () => boolean | Promise<boolean>
}

interface ModalInstance extends ModalOptions {
  id: number
  resolver?: (value: boolean) => void
}

interface ModalContextType {
  confirm: (options: ModalOptions) => Promise<boolean>
  modal: (options: ModalOptions) => number
  closeModal: (id?: number, result?: boolean) => void
  /**
   * Patch an open modal. Needed when content rendered inside the modal has to
   * change the chrome around it — e.g. the package form switching its own title
   * once the user picks a type.
   */
  updateModal: (id: number, patch: Partial<Omit<ModalOptions, 'id'>>) => void
}

interface ModalProviderProps {
  children: ReactNode
}

const ModalContext = createContext<ModalContextType | undefined>(undefined)

export const ModalProvider = ({ children }: ModalProviderProps) => {
  const [modals, setModals] = useState<ModalInstance[]>([])

  /**
   * Mirror of `modals` that a stale closure can still read correctly.
   *
   * `closeModal` is routinely frozen into a modal's own `description` — the
   * JSX is built once, when `modal()`/`confirm()` is called, and kept in state
   * from then on. The `closeModal` captured there belongs to the render that
   * OPENED the modal, whose `modals` array does not contain it yet. Reading
   * that captured array would look the modal up and miss, so its resolver
   * would never fire and the `await confirm(...)` would hang — the very bug
   * settling resolvers is meant to prevent.
   */
  const modalsRef = useRef<ModalInstance[]>(modals)
  // Assigned directly during render, not in a useEffect: effects fire
  // bottom-up, so a modal's own mount effect (closing itself as soon as it
  // appears) can run before an effect here would have synced the ref —
  // closeModal would then look the modal up in the stale array and miss it.
  modalsRef.current = modals
  const { t } = useTranslation()

  const confirm = (options: ModalOptions) => {
    return new Promise<boolean>((resolve) => {
      const id = Date.now()
      setModals((prev) => [...prev, { id, resolver: resolve, ...options }])
    })
  }

  const modal = (options: ModalOptions) => {
    const id = Date.now()
    setModals((prev) =>
      options.modalKey && prev.some((m) => m.modalKey === options.modalKey)
        ? prev
        : [...prev, { id, ...options }]
    )

    // When the call was de-duplicated this id matches no open modal, which only
    // makes `closeModal(id)` a no-op — the modal already on screen owns its own
    // close. Callers that need the live id should hold on to the first one.
    return id
  }

  // Stable identity: consumers patch from an effect, and an unstable callback in
  // that effect's deps would loop (patching re-renders the provider).
  const updateModal = useCallback((id: number, patch: Partial<Omit<ModalOptions, 'id'>>) => {
    setModals((prev) => prev.map((m) => (m.id === id ? { ...m, ...patch } : m)))
  }, [])

  /**
   * Resolvers are settled HERE, before the state update, not inside the
   * `setModals` updater. An updater must be pure: React may run it twice
   * (StrictMode) or throw the result away on a discarded render, and resolving
   * a caller's promise from inside it is a side effect riding on that.
   *
   * The lookup goes through `modalsRef`, never the captured `modals`, because
   * this callback is often an old one frozen into a modal's description — see
   * the ref's own note.
   *
   * Settling matters at all because dropping a pending `confirm()` resolver
   * leaves that promise hanging for the life of the page — and any caller doing
   * work around `await confirm(...)` (claiming a busy flag, holding a spinner)
   * hangs with it.
   */
  const closeModal = (id?: number, result = false) => {
    const open = modalsRef.current

    if (!id) {
      open.forEach((m) => m.resolver?.(result))
      setModals([])
      return
    }

    open.find((m) => m.id === id)?.resolver?.(result)
    setModals((prev) => prev.filter((m) => m.id !== id))
  }

  const handleClose = (m: ModalInstance) => {
    if (m.onBeforeClose) {
      const result = m.onBeforeClose()
      if (result instanceof Promise) {
        void result.then((canClose) => {
          if (canClose) closeModal(m.id, false)
        })
      } else if (result) {
        closeModal(m.id, false)
      }
    } else {
      closeModal(m.id, false)
    }
  }

  return (
    <ModalContext.Provider value={{ confirm, modal, closeModal, updateModal }}>
      {children}

      {modals.map((m) => (
        <Dialog
          key={m.id}
          open
          onClose={() => handleClose(m)}
          scroll="paper"
          slotProps={{
            paper: {
              sx: {
                gap: 3,
                borderRadius: 4,
                p: 6,
                maxWidth: (m.width ?? 738) + 'px',
                width: '100%',
                maxHeight: 'calc(100vh - 64px)',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
              },
            },
          }}
        >
          <DialogTitle
            typography="h5"
            fontSize="28px"
            sx={{
              padding: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center',
              fontWeight: 700,
              wordBreak: 'break-word',
              color: 'text.primary',
            }}
          >
            <span>{m.title}</span>
            <IconButton
              sx={{ p: 0, position: 'absolute', top: '16px', right: '16px' }}
              onClick={() => handleClose(m)}
            >
              <CloseIcon sx={{ fontSize: '36px', color: 'text.primary' }} />
            </IconButton>
          </DialogTitle>

          <DialogContent
            sx={{
              p: 0,
              flex: 1,
              overflowY: 'auto',
              minHeight: 0,
              '&.MuiDialogContent-root': {
                pt: 1,
              },
            }}
          >
            <DialogContentText
              variant="body1"
              component="div"
              sx={{ color: 'text.primary', p: 0, textAlign: 'center', ...m.sx }}
            >
              {m.description}
            </DialogContentText>
          </DialogContent>

          {!m.hideActions ? (
            <DialogActions sx={{ justifyContent: 'center', gap: '10px', p: 0, width: '100%' }}>
              {!m.hideCancelButton && (
                <Button
                  variant="outlined"
                  onClick={() => closeModal(m.id, false)}
                  sx={{ flex: 1, height: 40, maxWidth: 332 }}
                >
                  {m.cancelLabel || t('cancel')}
                </Button>
              )}

              {!m.hideConfirmButton && (
                <Button
                  variant="contained"
                  onClick={() => closeModal(m.id, true)}
                  sx={{ flex: 1, margin: '0 !important', height: 40, maxWidth: 332 }}
                >
                  {m.confirmLabel || t('confirm')}
                </Button>
              )}
            </DialogActions>
          ) : null}
        </Dialog>
      ))}
    </ModalContext.Provider>
  )
}

export const useModal = () => {
  const context = useContext(ModalContext)
  if (!context) throw new Error('useModal must be used within a ModalProvider')
  return context
}
